<template>
  <div class="dashboard-container">
    <!-- Full-screen Background Map -->
    <div class="map-layer">
      <component 
        :is="currentMapComponent" 
        ref="mapRef" 
        :stations="filteredStations" 
        :is3D="baseMapMode === 'outline3d'"
        @province-click="handleProvinceClick"
        @station-click="handleStationClick" 
        @cluster-click="handleClusterClick"
      />
    </div>

    <!-- UI Overlay Layer -->
    <div class="ui-layer">
      <!-- Header -->
      <header class="dashboard-header glass-panel">
        <div class="header-left">
          <div class="brand-lockup">
            <img :src="dashboardLogoSrc" alt="地脉动监测预警系统" class="sys-logo" />
          </div>
        </div>
        <div class="header-center">
          <h1 class="glow-text">地脉动监测预警系统</h1>
        </div>
        <div class="header-right">
          <span class="time">{{ currentTime }}</span>
          <button class="dashboard-icon-button" type="button" :aria-label="themeLabel" @click="themeStore.toggle()">
            <Sun v-if="themeStore.theme === 'dark'" :size="19" />
            <Moon v-else :size="19" />
          </button>
          <UserMenu />
        </div>
      </header>

      <!-- Main Content Grid (Desktop) -->
      <main class="dashboard-main desktop-layout">
        <!-- Left Panel -->
        <aside class="panel-left">
          <div class="glass-panel wrapper">
            <h2 class="panel-title">综合接入情况</h2>
            <div class="charts-placeholder">
              <div class="stat-box">项目数 <span class="cyan">{{ dashboardProjectCount }}</span></div>
              <div class="stat-box">设备数 <span class="cyan">{{ dashboardDeviceCount }}</span></div>
            </div>
            
            <!-- 大屏网格入口按钮 -->
            <button class="grid-nav-btn mt-15" @click="goToGridDashboard">
              多设备视图
            </button>
          </div>
          <div class="glass-panel wrapper flex-grow">
            <h2 class="panel-title">监测站点列表 ({{ filteredStations.length }})</h2>
            <ul class="device-list">
              <li v-for="(station, index) in filteredStations" :key="index" :class="['device-item', station.status]"
                style="cursor: pointer;" @click.stop="handleStationListItemClick(station)">
                <span class="device-name">{{ station.name }}</span>
                <span class="device-type-tag">{{ station.device_type === 'camera' ? '监控' : '监测' }}</span>
                <span class="device-status">{{ getStatusText(station.status) }}</span>
              </li>
            </ul>
          </div>
        </aside>

        <!-- Center Space (Empty) to let map show through -->
        <section class="panel-center empty-space"></section>

        <!-- Right Panel -->
        <aside class="panel-right" :class="{ 'has-selected-device': selectedStationData }">
          <div class="glass-panel wrapper weather-panel">
            <h2 class="panel-title">实时天气</h2>
            <div class="weather-widget">
              <div class="weather-location">{{ weatherData.location }}</div>
              <div class="weather-main">
                <CloudRain class="weather-icon-svg" :size="45" aria-hidden="true" />
                <span class="weather-temp">{{ weatherData.temperature }}°C</span>
              </div>
              <div class="weather-desc">{{ weatherData.description }}</div>
              <div class="weather-details">
                <div class="detail-item">
                  <span class="label">湿度</span>
                  <span class="val">{{ weatherData.humidity }}%</span>
                </div>
                <div class="detail-item">
                  <span class="label">风速</span>
                  <span class="val">{{ weatherData.windSpeed }} km/h</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 选中的设备简报卡片 -->
          <div v-if="selectedStationData" class="glass-panel wrapper selected-device-card animate-slide-in">
            <div class="card-header-row">
              <h2 class="panel-title">当前选中设备</h2>
              <button class="close-card-btn" aria-label="关闭设备卡片" @click.stop="closeSelectedCard"><X :size="18" /></button>
            </div>
            <div class="device-info-brief">
              <div class="brief-row">
                <span class="label">名称:</span>
                <span class="value">{{ selectedStationData.name }}</span>
              </div>
              <div class="brief-row">
                <span class="label">状态:</span>
                <span :class="['value', 'status-tag', selectedStationData.status]">
                  {{ getStatusText(selectedStationData.status) }}
                </span>
              </div>
              <div class="brief-row">
                <span class="label">编号:</span>
                <span class="value sid">{{ selectedStationData.sid_no }}</span>
              </div>
              <div class="brief-row">
                <span class="label">类型:</span>
                <span class="value">{{ selectedStationData.device_type === 'camera' ? '监控设备' : '监测设备' }}</span>
              </div>
            </div>
            <button class="monitor-nav-btn" @click="navigateToMonitor(selectedStationData.sid_no)">
              进入设备详情 <ArrowRight :size="18" />
            </button>

          </div>

          <div v-else class="glass-panel wrapper flex-grow">
            <h2 class="panel-title">项目</h2>
            <ul class="project-list">
              <li v-for="project in projectStats" :key="project.id"
                  :class="['project-item', project.status, { active: selectedProjectId === project.id }]"
                  role="button" :aria-pressed="selectedProjectId === project.id" tabindex="0"
                  @click="handleProjectClick(project)"
                  @keydown.enter.prevent="handleProjectClick(project)"
                  @keydown.space.prevent="handleProjectClick(project)">
                <span class="project-name">{{ project.name }}</span>
                <span class="station-count"><strong>{{ project.count }}</strong> 个站点</span>
                <span class="station-count" :title="microseismicCountError">近24小时微震 <strong>{{ project.eventCount ?? '—' }}</strong> 个</span>
              </li>
            </ul>
          </div>
        </aside>
      </main>
    </div>

    <!-- Floating Map Mode Switcher -->
    <div class="map-controls-overlay">
      <MapModeSwitcher v-model="baseMapMode" />
    </div>

    <!-- Modern Mobile Bottom Drawer -->
    <div
      class="mobile-drawer"
      :class="{ 'drawer-open': mobileDrawerOpen }"
      @wheel.stop
      @touchmove.stop
    >
      <div class="drawer-handle-area" @click="mobileDrawerOpen = !mobileDrawerOpen">
        <div class="capsule-handle"></div>
      </div>
      <div class="drawer-content">
        <div class="drawer-scroll-area" @wheel.stop @touchmove.stop>
          <div class="drawer-section modern-stats">
            <h2 class="drawer-title">综合接入情况</h2>
            <div class="drawer-stats">
              <div class="stat-box glass-card">
                <span class="stat-label">项目数</span>
                <span class="stat-value cyan">{{ dashboardProjectCount }}</span>
              </div>
              <div class="stat-box glass-card">
                <span class="stat-label">设备数</span>
                <span class="stat-value cyan">{{ dashboardDeviceCount }}</span>
              </div>
            </div>
          </div>

          <div class="drawer-section mobile-primary-actions">
            <button class="grid-nav-btn mobile-grid-entry" type="button" @click="goToGridDashboard">
              多设备视图
            </button>
          </div>
          
          <div class="drawer-section weather-section">
            <h2 class="drawer-title">实时天气 · {{ weatherData.location }}</h2>
            <div class="drawer-weather glass-card">
              <div class="weather-left">
                <CloudRain class="weather-icon-svg" :size="38" aria-hidden="true" />
                <span class="weather-temp">{{ weatherData.temperature }}°C</span>
              </div>
              <span class="weather-desc">{{ weatherData.description }}</span>
            </div>
          </div>

          <div v-if="selectedStationData" class="drawer-section selected-device-section">
            <h2 class="drawer-title">当前选中设备</h2>
            <div class="glass-card mobile-selected-device-card">
              <div class="card-header-row">
                <h3 class="mobile-selected-title">{{ selectedStationData.name }}</h3>
                <button class="close-card-btn" type="button" aria-label="关闭设备卡片" @click.stop="closeSelectedCard">
                  <X :size="18" />
                </button>
              </div>
              <div class="device-info-brief">
                <div class="brief-row">
                  <span class="label">状态:</span>
                  <span :class="['value', 'status-tag', selectedStationData.status]">
                    {{ getStatusText(selectedStationData.status) }}
                  </span>
                </div>
                <div class="brief-row">
                  <span class="label">编号:</span>
                  <span class="value sid">{{ selectedStationData.sid_no }}</span>
                </div>
                <div class="brief-row">
                  <span class="label">类型:</span>
                  <span class="value">{{ selectedStationData.device_type === 'camera' ? '监控设备' : '监测设备' }}</span>
                </div>
              </div>
              <button class="monitor-nav-btn" type="button" @click="navigateToMonitor(selectedStationData.sid_no)">
                进入设备详情 <ArrowRight :size="18" />
              </button>
            </div>
          </div>

          <div class="drawer-section list-section">
            <h2 class="drawer-title">监测站点 ({{ filteredStations.length }})</h2>
            <ul class="device-list">
              <li v-for="project in groupedStations" :key="project.id" class="project-group">
                <div class="project-header glass-card" :class="{ active: selectedProjectId === project.id }"
                  role="button" :aria-pressed="selectedProjectId === project.id" tabindex="0"
                  @click="handleProjectClick(project)"
                  @keydown.enter.prevent="handleProjectClick(project)"
                  @keydown.space.prevent="handleProjectClick(project)">
                  <div class="proj-header-left">
                    <span class="toggle-icon">{{ expandedProjects.includes(project.id) ? '▼' : '▶' }}</span>
                    <span class="project-name">{{ project.name }}</span>
                  </div>
                  <span class="station-count"><strong>{{ project.stations.length }}</strong> 台设备</span>
                  <span class="station-count" :title="microseismicCountError">近24小时微震 <strong>{{ project.eventCount ?? '—' }}</strong> 个</span>
                </div>
                <ul v-show="expandedProjects.includes(project.id)" class="project-stations">
                  <li v-for="(station, index) in project.stations" :key="index" :class="['device-item', station.status]"
                    @click.stop="handleStationListItemClick(station)">
                    <span class="device-type-dot" :class="station.device_type"></span>
                    <span class="device-name">{{ station.name }}</span>
                    <span class="device-status">{{ getStatusText(station.status) }}</span>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'
import { summaryCounts } from '@/utils/microseismicTimeline.mjs'
import MapComponent from '../components/MapComponent.vue'
import MobileMapComponent from '../components/MobileMapComponent.vue'
import TiandituJsSatelliteMapComponent from '../components/TiandituJsSatelliteMapComponent.vue'
import UserMenu from '../components/UserMenu.vue'
import MapModeSwitcher from '../components/MapModeSwitcher.vue'
import { ArrowRight, CloudRain, Moon, Sun, X } from '@lucide/vue'
import { useThemeStore } from '../store/theme'
import { useAuthStore } from '../store/auth'
import { DASHBOARD_HISTORY_KEY, dashboardOwner, dashboardSnapshot, legacyDashboardState, cleanDashboardQuery, saveDashboardHistory, resolveDashboardSelection, dashboardGridLocation, projectForStation, stationFromMapSelection } from '../utils/dashboardNavigation.mjs'
import logoDarkBg from '../assets/logo_dark_bg.svg'
import logoLightBg from '../assets/logo_light_bg.svg'
import { provinceNameForPoint } from '../utils/provinceLookup'

const NATIONAL_PROVINCE = '全国'

export default {
  name: 'DashboardView',
  components: {
    MapComponent,
    MobileMapComponent,
    TiandituJsSatelliteMapComponent,
    UserMenu,
    MapModeSwitcher,
    ArrowRight,
    CloudRain,
    Moon,
    Sun,
    X
  },
  setup() {
    return { themeStore: useThemeStore(), authStore: useAuthStore() }
  },
  data() {
    return {
      currentTime: new Date().toLocaleTimeString(),
      mobileDrawerOpen: false,
      selectionReady: false,
      selectionOwner: '',
      viewActive: false,
      currentProvince: NATIONAL_PROVINCE,
      baseMapMode: 'outline', // 'outline' | 'satellite'
      selectedProjectId: null, // 当前选中的项目 ID
      selectedStationData: null, // 当前选中的站点数据
      envelopeDeviceLevelMap: {}, // sid_no -> normal/yellow/orange/red
      // 大屏默认取近 7 天（很多设备数据不连续，24h 会导致 summary 为空）
      envelopeTimeRange: '7d',
      expandedProjects: [],
      weatherData: {
        location: '中国',
        temperature: '--',
        icon: '⌛',
        description: '加载中...',
        humidity: '--',
        windSpeed: '--'
      },
      projects: [],
      microseismicCounts: {},
      microseismicCountError: '',
      stations: [],
      dashboardStatsLoaded: false,
      stationPollTimer: null,
      stationFetchInFlight: false,
      pageVersion: 0,
      accessRevoked: false,
      pageHidden: typeof document !== 'undefined' && document.hidden,
      stationController: null,
      weatherController: null,
      provinceLookupCache: {}
    }
  },
  computed: {
    navigationOwner() {
      return dashboardOwner(this.authStore.userInfo)
    },
    navigationSnapshot() {
      return dashboardSnapshot(this.navigationOwner, {
        province: this.currentProvince,
        projectId: this.selectedProjectId,
        station: this.selectedStationData?.sid_no,
        mapMode: this.baseMapMode
      })
    },
    dashboardLogoSrc() {
      return this.themeStore.theme === 'dark' ? logoDarkBg : logoLightBg
    },
    themeLabel() {
      return this.themeStore.theme === 'dark' ? '切换到浅色主题' : '切换到深色主题'
    },
    currentMapComponent() {
      if (this.baseMapMode === 'satellite') return 'TiandituJsSatelliteMapComponent'
      
      // Both outline and outline3d use MapComponent (Desktop) or MobileMapComponent
      return this.$device?.isTablet()
        ? 'MobileMapComponent'
        : (this.$device?.isMobile() ? 'MobileMapComponent' : 'MapComponent')
    },
    filteredStations() {
      let list = this.stations
      if (this.currentProvince && this.currentProvince !== NATIONAL_PROVINCE) {
        list = list.filter(s => s.province === this.currentProvince)
      }
      if (this.selectedProjectId != null) {
        list = list.filter(s => String(s.project_id) === String(this.selectedProjectId))
      }
      return list
    },
    dashboardProjectCount() {
      return this.dashboardStatsLoaded ? this.projects.length : '--'
    },
    dashboardDeviceCount() {
      return this.dashboardStatsLoaded ? this.stations.length : '--'
    },
    projectStats() {
      const stats = {};
      const baseList = (!this.currentProvince || this.currentProvince === NATIONAL_PROVINCE)
          ? this.stations
          : this.stations.filter(s => s.province === this.currentProvince);

      this.projects.forEach(project => {
        if (!project?.name) return
        stats[String(project.id)] = {
          id: project.id,
          eventCount: this.microseismicCounts[String(project.id)],
          name: project.name,
          count: 0,
          status: 'normal',
          stations: []
        }
      })

      baseList.forEach(s => {
        const project = stats[String(s.project_id)];
        if (!project) return
        project.count++;
        project.stations.push(s);

        // 状态冒泡：红 > 橙 > 黄 > 正常（任一指标取最大）
        const rank = this.severityRank(s.status)
        const curRank = this.severityRank(project.status)
        if (rank > curRank) project.status = s.status
      });
      return Object.values(stats).sort((a, b) => a.name.localeCompare(b.name));
    },
    groupedStations() {
      return this.projectStats.map(item => ({
        id: item.id,
        name: item.name,
        eventCount: item.eventCount,
        status: item.status,
        stations: item.stations || []
      }))
    }
  },
  watch: {
    navigationSnapshot() {
      this.persistSelection()
    },
    navigationOwner() {
      this.selectionReady = false
      this.projects = []
      this.stations = []
      this.selectedProjectId = null
      this.selectedStationData = null
      this.currentProvince = NATIONAL_PROVINCE
      this.dashboardStatsLoaded = false
      if (this.navigationOwner) this.fetchStations()
    },
    '$route'() {
      if (this.$route.name === 'Dashboard' && this.selectionReady) this.restoreSelection()
    },
    baseMapMode() {
      // component switched, re-apply selected province to new map instance
      this.scheduleMapStateRehydrate()
    }
  },
  mounted() {
    this.viewActive = true
    // 先把旧链接搬进当前历史记录，再读取数据；暂时断网不会丢掉待恢复的选择。
    const legacy = legacyDashboardState(this.$route.query, this.navigationOwner)
    if (legacy) {
      this.$router.replace({
        name: 'Dashboard', query: cleanDashboardQuery(this.$route.query), hash: this.$route.hash,
        state: { [DASHBOARD_HISTORY_KEY]: legacy }
      }).then(() => { if (this.viewActive) this.fetchStations() })
    } else {
      this.fetchStations()
    }

    document.addEventListener('visibilitychange', this.handlePageVisibility)
    document.addEventListener('microquake:auth-expired', this.handleAuthExpired)

    // 初始加载全国中心天气，不把地图默认锁定到某个省份。
    this.fetchWeather(35.8617, 104.1954, false);

    this.startPageTimers()
  },
  beforeUnmount() {
    this.viewActive = false
    this.pageVersion++
    this.stationController?.abort()
    this.weatherController?.abort()
    document.removeEventListener('visibilitychange', this.handlePageVisibility)
    document.removeEventListener('microquake:auth-expired', this.handleAuthExpired)
    if (this.timer) {
      clearInterval(this.timer)
    }
    if (this.stationPollTimer) {
      clearInterval(this.stationPollTimer)
    }
  },
  methods: {
    handleAuthExpired() { this.accessRevoked = true; this.handlePageVisibility() },
    startPageTimers() {
      clearInterval(this.timer)
      clearInterval(this.stationPollTimer)
      if (!this.viewActive || this.accessRevoked || document.hidden) return
      this.currentTime = new Date().toLocaleTimeString()
      this.timer = setInterval(() => { this.currentTime = new Date().toLocaleTimeString() }, 1000)
      this.stationPollTimer = setInterval(() => this.fetchStations(), 5000)
    },
    handlePageVisibility() {
      const hidden = document.hidden || this.accessRevoked
      if (this.pageHidden === hidden) return
      this.pageHidden = hidden
      this.pageVersion++
      this.stationController?.abort()
      this.weatherController?.abort()
      this.stationFetchInFlight = false
      this.startPageTimers()
      if (!document.hidden && !this.accessRevoked && this.viewActive) {
        this.fetchStations()
        if (this.lastWeatherQuery) this.fetchWeather(...this.lastWeatherQuery)
      }
    },
    persistSelection() {
      if (this.viewActive && this.selectionReady && this.navigationOwner && this.selectionOwner === this.navigationOwner && this.$route.name === 'Dashboard') {
        saveDashboardHistory(window.history, this.navigationSnapshot)
      }
    },
    restoreSelection() {
      const selection = resolveDashboardSelection(
        window.history.state?.[DASHBOARD_HISTORY_KEY], this.navigationOwner, this.projects, this.stations
      )
      this.currentProvince = selection.province
      this.selectedProjectId = selection.project?.id ?? null
      this.selectedStationData = selection.station
      this.baseMapMode = selection.mapMode
      this.selectionOwner = this.navigationOwner
      this.selectionReady = true
      this.persistSelection()
      this.scheduleMapStateRehydrate()
      if (selection.station?.value) {
        this.fetchWeather(selection.station.value[1], selection.station.value[0], true)
      }
    },
    severityRank(level) {
      const map = { normal: 0, yellow: 1, orange: 2, red: 3 }
      return map[level] ?? 0
    },
    mapLevelToStatus(level) {
      const l = (level || 'normal').toLowerCase()
      if (['red', 'orange', 'yellow'].includes(l)) return l
      return 'normal'
    },
    scheduleMapStateRehydrate() {
      this.$nextTick(() => {
        const map = this.$refs.mapRef
        if (!map) return
        const provinceName = this.currentProvince || NATIONAL_PROVINCE
        if (this.selectedStationData && typeof map.zoomToStation === 'function') {
          if (typeof map.syncViewState === 'function') {
            map.syncViewState({ provinceName })
          }
          map.zoomToStation(this.selectedStationData)
          return
        }
        if (this.selectedProjectId != null && typeof map.zoomToProjectCluster === 'function') {
          const projectStations = this.stationsForProjectId(this.selectedProjectId)
          if (projectStations.length) {
            if (typeof map.syncViewState === 'function') {
              map.syncViewState({ provinceName })
            }
            map.zoomToProjectCluster(projectStations)
            return
          }
        }
        if (typeof map.syncViewState === 'function') {
          map.syncViewState({ provinceName })
          return
        }
        if (typeof map.zoomToProvince === 'function') {
          map.zoomToProvince(provinceName)
        }
      })
    },
    toggleBaseMap() {
      // Deprecated in favor of MapModeSwitcher
    },
    goToGridDashboard() {
      this.$router.push(dashboardGridLocation(this.selectedProjectId ?? this.selectedStationData?.project_id))
    },
    closeSelectedCard() {
      this.selectedStationData = null;
    },
    async resolveDeviceProvince(device) {
      const cacheKey = `${device.longitude},${device.latitude}`
      if (this.provinceLookupCache[cacheKey]) return this.provinceLookupCache[cacheKey]
      try {
        const province = await provinceNameForPoint(device.longitude, device.latitude)
        const resolved = province || device.province || NATIONAL_PROVINCE
        this.provinceLookupCache[cacheKey] = resolved
        return resolved
      } catch (error) {
        console.warn('province lookup failed:', error)
        const fallback = device.province || NATIONAL_PROVINCE
        this.provinceLookupCache[cacheKey] = fallback
        return fallback
      }
    },
    projectProvince(project) {
      const stations = Array.isArray(project?.stations) && project.stations.length
        ? project.stations
        : this.stationsForProjectId(project?.id ?? this.selectedProjectId)
      const provinceCounts = stations.reduce((counts, station) => {
        const province = station?.province
        if (province && province !== NATIONAL_PROVINCE) counts[province] = (counts[province] || 0) + 1
        return counts
      }, {})
      const [province] = Object.entries(provinceCounts).sort((a, b) => b[1] - a[1])[0] || []
      return province || NATIONAL_PROVINCE
    },
    setProvinceFromProject(project) {
      const province = this.projectProvince(project)
      if (province && this.currentProvince !== province) {
        this.currentProvince = province
      }
      if (province && province !== NATIONAL_PROVINCE) {
        this.weatherData.location = province
      }
    },
    setProvinceFromStation(station) {
      if (!station?.province || station.province === NATIONAL_PROVINCE) return
      this.currentProvince = station.province
    },
    syncMapProvinceSelection() {
      const map = this.$refs.mapRef
      if (map && typeof map.syncViewState === 'function') {
        map.syncViewState({ provinceName: this.currentProvince || NATIONAL_PROVINCE })
      }
    },
    stationsForProjectId(projectId) {
      return this.stations.filter(station => String(station.project_id) === String(projectId))
    },
    reconcileProvinceWithStations() {
      if (this.selectedStationData) {
        const freshStation = this.stations.find(station => station.sid_no === this.selectedStationData.sid_no)
        if (freshStation) {
          this.selectedStationData = freshStation
          this.setProvinceFromStation(freshStation)
          return
        }
        this.selectedStationData = null
        this.selectedProjectId = null
        this.currentProvince = NATIONAL_PROVINCE
      }
      if (this.selectedProjectId != null) {
        const project = this.projectStats.find(item => String(item.id) === String(this.selectedProjectId))
        if (project) {
          this.setProvinceFromProject(project)
          return
        }
      }
      if (this.currentProvince && this.currentProvince !== NATIONAL_PROVINCE) {
        const hasCurrentProvinceStation = this.stations.some(station => station.province === this.currentProvince)
        if (!hasCurrentProvinceStation) this.currentProvince = NATIONAL_PROVINCE
      }
    },
    openMobileDrawerForSelection() {
      if (typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches) {
        this.mobileDrawerOpen = true
      }
    },
    async fetchStations() {
      if (this.accessRevoked || (typeof document !== 'undefined' && document.hidden) || this.stationFetchInFlight || !this.viewActive || !this.navigationOwner) return
      this.stationFetchInFlight = true
      const version = this.pageVersion
      const controller = new AbortController()
      this.stationController = controller
      const owner = this.navigationOwner
      try {
        const [devicesResult, summaryResult, projectsResult, eventSummaryResult] = await Promise.allSettled([
          request.get('/devices/list/', { signal: controller.signal }),
          request.get('/devices/envelope-summary/', { params: { time_range: this.envelopeTimeRange }, signal: controller.signal }),
          request.get('/management/projects/', { signal: controller.signal }),
          request.get('/microseismic/events/summary/', { silentError: true, signal: controller.signal }),
        ])
        if (controller.signal.aborted || version !== this.pageVersion || !this.viewActive || owner !== this.navigationOwner) return
        this.microseismicCounts = eventSummaryResult.status === 'fulfilled' ? summaryCounts(eventSummaryResult.value.data, 'last_24h_count') : {}
        this.microseismicCountError = eventSummaryResult.status === 'fulfilled' ? '' : '微震事件计数读取失败'
        if (devicesResult.status !== 'fulfilled') {
          console.error('Failed to fetch device list:', devicesResult.reason)
          return
        }
        const devicesResp = devicesResult.value
        const summaryResp = summaryResult.status === 'fulfilled' ? summaryResult.value : null
        if (summaryResult.status !== 'fulfilled') {
          console.warn('Envelope summary unavailable (stations still shown):', summaryResult.reason)
        }
        if (projectsResult.status !== 'fulfilled' || !Array.isArray(projectsResult.value?.data)) {
          console.warn('Project list unavailable (dashboard stats unchanged):', projectsResult.reason)
          return
        }
        this.projects = projectsResult.value.data
        const accessibleProjectIds = new Set(this.projects.map(project => project.id))

        const summary = summaryResp?.data || {}
        const devLevels = {}
        if (Array.isArray(summary.devices)) {
          summary.devices.forEach((d) => {
            if (d && d.device_sid) devLevels[d.device_sid] = d.max_alert_level || 'normal'
          })
        }
        this.envelopeDeviceLevelMap = devLevels

        const response = devicesResp
        if (response.data && Array.isArray(response.data)) {
          const normalizedDevices = response.data
            .map(device => ({
              ...device,
              project_id: device.project_id ?? device.project
            }))
            .filter(device => device.project_id != null && accessibleProjectIds.has(device.project_id))
          const stations = await Promise.all(normalizedDevices.map(async device => {
            const sid = device.sid_no
            const lv = this.envelopeDeviceLevelMap[sid] || 'normal'
            const province = await this.resolveDeviceProvince(device)
            return ({
            name: device.name || device.sid_no,
            value: [device.longitude, device.latitude, 10], // Default weight 10
            status: this.mapLevelToStatus(lv),
            province,
            sid_no: device.sid_no, // Keep original ID
            project_id: device.project_id,
            microseismic_event_count_24h: this.microseismicCounts[String(device.project_id)],
            project_name: device.project_name,
            device_type: device.device_type || 'sensor'
          })
          }))
          if (controller.signal.aborted || version !== this.pageVersion || !this.viewActive || owner !== this.navigationOwner) return
          this.stations = stations
          if (this.selectedProjectId != null && !this.projects.some(project => String(project.id) === String(this.selectedProjectId))) {
            this.selectedProjectId = null
          }
          this.reconcileProvinceWithStations()
          this.dashboardStatsLoaded = true
          if (!this.selectionReady) this.restoreSelection()
        }
      } catch (error) {
        if (controller.signal.aborted || version !== this.pageVersion) return
        console.error('Failed to fetch stations:', error);
      } finally {
        if (version === this.pageVersion && this.stationController === controller) {
          this.stationFetchInFlight = false
          if (this.viewActive && this.navigationOwner && owner !== this.navigationOwner) this.fetchStations()
        }
      }
    },
    getStatusText(statusCode) {
      const map = {
        'normal': '正常',
        'yellow': '黄色预警',
        'orange': '橙色预警',
        'red': '红色预警'
      }
      return map[statusCode] || '未知状态';
    },
    handleProvinceClick(payload) {
      this.currentProvince = payload.name;
      this.selectedProjectId = null; // 仅选择省份时不推断项目
      this.selectedStationData = null;
      this.weatherData.location = payload.name === NATIONAL_PROVINCE ? '中国' : payload.name;

      if (payload.center) {
        this.fetchWeather(payload.center[1], payload.center[0], false);
      }
    },
    handleStationClick(payload) {
      this.weatherData.location = payload.name;
      if (payload.gps) {
        this.fetchWeather(payload.gps[1], payload.gps[0], true);
      }
      
      const station = stationFromMapSelection(payload, this.stations);
      if (station) {
         this.selectedStationData = station; // 设置选中设备数据
         this.setProvinceFromStation(station);
         this.$nextTick(() => {
           this.syncMapProvinceSelection()
           if (this.$refs.mapRef && typeof this.$refs.mapRef.zoomToStation === 'function') {
             this.$refs.mapRef.zoomToStation(station)
           }
         })
         this.openMobileDrawerForSelection();
         this.selectedProjectId = projectForStation(station, this.projects)?.id ?? null;
      }
    },
    navigateToMonitor(sid) {
      this.persistSelection()
      this.$router.push({ name: 'Monitor', params: { sid }, state: { [DASHBOARD_HISTORY_KEY]: this.navigationSnapshot } })
    },
    handleClusterClick(payload) {
      if (payload.projectId != null) {
        this.selectedStationData = null;
        const project = this.projectStats.find(item => String(item.id) === String(payload.projectId))
        this.selectedProjectId = project?.id ?? null;
        if (project) this.setProvinceFromProject(project)
      }
    },
    handleStationListItemClick(station) {
      this.weatherData.location = station.name;
      this.selectedStationData = station;
      this.setProvinceFromStation(station);
      this.openMobileDrawerForSelection();
      if (station.value) {
        this.fetchWeather(station.value[1], station.value[0], true);
      }

      this.selectedProjectId = projectForStation(station, this.projects)?.id ?? null;

      this.$nextTick(() => {
        this.syncMapProvinceSelection()
        if (this.$refs.mapRef && typeof this.$refs.mapRef.zoomToStation === 'function') {
          this.$refs.mapRef.zoomToStation(station);
        }
      });
    },
    resetDashboardHomeView() {
      this.currentProvince = NATIONAL_PROVINCE;
      this.selectedProjectId = null;
      this.selectedStationData = null;
      this.weatherData.location = '中国';
      this.persistSelection();

      this.$nextTick(() => {
        const map = this.$refs.mapRef;
        if (!map) return;
        if (typeof map.syncViewState === 'function') {
          map.syncViewState({ provinceName: this.currentProvince });
          return;
        }
        if (typeof map.zoomToProvince === 'function') {
          map.zoomToProvince(this.currentProvince);
        }
      });
    },
    handleProjectClick(project) {
      if (project?.name) {
        if (this.expandedProjects.includes(project.id)) {
          this.expandedProjects = this.expandedProjects.filter(id => id !== project.id)
        } else {
          this.expandedProjects.push(project.id)
        }
      }
      if (String(this.selectedProjectId) === String(project.id)) {
        this.resetDashboardHomeView();
      } else {
        // 选中项目
        this.selectedStationData = null;
        this.selectedProjectId = project.id;
        this.setProvinceFromProject(project);
        
        const projectStations = this.stationsForProjectId(project.id)
        this.$nextTick(() => {
          this.syncMapProvinceSelection()
          if (this.$refs.mapRef && typeof this.$refs.mapRef.zoomToProjectCluster === 'function') {
            this.$refs.mapRef.zoomToProjectCluster(projectStations);
          }
        })
      }
    },
    async fetchWeather(lat, lon, shouldGeocode = true) {
      this.lastWeatherQuery = [lat, lon, shouldGeocode]
      if (!this.viewActive || this.accessRevoked || document.hidden) return
      this.weatherController?.abort()
      const controller = new AbortController()
      this.weatherController = controller
      const version = this.pageVersion
      const weatherTimeoutMs = 9000
      const weatherRequestOptions = {
        timeout: weatherTimeoutMs,
        silentError: true,
        signal: controller.signal,
      }
      try {
        const tasks = [
          request.get('/weather/forecast/', {
            params: {
              latitude: lat,
              longitude: lon,
              current_weather: true,
              hourly: 'relative_humidity_2m,wind_speed_10m',
            },
            ...weatherRequestOptions,
          }),
        ]
        if (shouldGeocode) {
          tasks.push(
            request.get('/weather/reverse-geocode/', {
              params: { lat, lon, format: 'json', 'accept-language': 'zh-CN' },
              ...weatherRequestOptions,
            })
          )
        }

        const results = await Promise.allSettled(tasks)
        if (!this.viewActive || controller.signal.aborted || version !== this.pageVersion) return
        const weatherResult = results[0]
        const geoResult = shouldGeocode ? results[1] : null

        if (weatherResult.status === 'fulfilled' && weatherResult.value.data?.current_weather) {
          const cw = weatherResult.value.data.current_weather
          this.weatherData.temperature = Math.round(cw.temperature)
          this.weatherData.icon = this.getWeatherIcon(cw.weathercode)
          this.weatherData.description = this.getWeatherDesc(cw.weathercode)
          this.weatherData.windSpeed = cw.windspeed

          if (weatherResult.value.data.hourly?.relative_humidity_2m) {
            this.weatherData.humidity = weatherResult.value.data.hourly.relative_humidity_2m[0]
          }
        } else {
          this.weatherData.description = '天气暂不可用'
        }

        if (geoResult?.status === 'fulfilled' && geoResult.value.data?.address) {
          const addr = geoResult.value.data.address
          const city = addr.city || addr.town || addr.village || addr.county || ''
          const suburb = addr.suburb || addr.district || ''
          if (city || suburb) {
            this.weatherData.location = `${city}${suburb ? ' · ' + suburb : ''}`
          }
        }
      } catch (err) {
        if (!this.viewActive || controller.signal.aborted || version !== this.pageVersion) return
        console.warn('Weather fetch failed:', err)
        this.weatherData.description = '天气暂不可用'
      }
    },
    getWeatherIcon(code) {
      const icons = {
        0: '☀️',
        1: '🌤️', 2: '⛅', 3: '☁️',
        45: '🌫️', 48: '🌫️',
        51: '🌧️', 53: '🌧️', 55: '🌧️',
        61: '🌧️', 63: '🌧️', 65: '🌧️',
        71: '❄️', 73: '❄️', 75: '❄️',
        80: '🌦️', 81: '🌦️', 82: '🌦️',
        95: '⛈️', 96: '⛈️', 99: '⛈️'
      };
      return icons[code] || '🌡️';
    },
    getWeatherDesc(code) {
      const descs = {
        0: '晴朗',
        1: '晴间多云', 2: '多云', 3: '阴天',
        45: '有雾', 48: '浓雾',
        51: '毛毛雨', 53: '细雨', 55: '小雨',
        61: '阵雨', 63: '中雨', 65: '大雨',
        71: '小雪', 73: '中雪', 75: '大雪',
        80: '阵雷雨', 81: '雷阵雨', 82: '雷暴',
        95: '雷阵雨', 96: '雷阵雨伴有冰雹', 99: '强雷阵雨伴有冰雹'
      };
      return descs[code] || '未知';
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  position: relative;
  height: 100dvh;
  width: 100%;
  max-width: 100%;
  background: linear-gradient(135deg, var(--dash-bg-start) 0%, var(--dash-bg-end) 100%);
  overflow: hidden;
}

.map-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  background: linear-gradient(135deg, var(--dash-bg-start) 0%, var(--dash-bg-end) 100%);
}

.map-layer::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 2;
  background: var(--dash-map-vignette);
}

.calibrate-btn {
  margin-top: 8px;
}

.ui-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 1rem 1.5rem;
  box-sizing: border-box;
  z-index: 10;
  /* Put UI on top of map */
  pointer-events: none;
  /* Let clicks pass through to map where UI is transparent */
}

/* Re-enable pointer events for actual UI panels so buttons/scrolls work */
.dashboard-header,
.panel-left,
.panel-right {
  pointer-events: auto;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 0.8rem 1rem;
  flex: 0 0 auto;
  position: relative;
  overflow: visible !important;
  z-index: 100;
  min-width: 0;
}

.dashboard-header h1 {
  margin: 0;
  max-width: min(760px, 44vw);
  font-size: clamp(1.2rem, 1.65vw, 1.55rem);
  font-weight: 700;
  letter-spacing: 1px;
  line-height: 1.25;
  text-align: center;
}

.brand-lockup {
  display: flex;
  align-items: center;
  min-width: 0;
}

.sys-logo {
  height: 44px;
  width: auto;
  max-width: min(360px, 32vw);
  flex-shrink: 0;
  filter: var(--dash-logo-glow);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.header-center {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.header-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
  min-width: 0;
}

.dashboard-icon-button {
  display: inline-flex;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: var(--dash-icon-button-color);
  background: var(--dash-icon-button-bg);
  border: 1px solid var(--dash-icon-button-border);
  border-radius: 8px;
}

.dashboard-icon-button:hover {
  color: var(--dash-icon-button-hover-color);
  background: var(--dash-icon-button-hover-bg);
}

.weather-icon-svg {
  flex: 0 0 auto;
  color: var(--dash-weather-icon);
  filter: drop-shadow(0 3px 8px color-mix(in srgb, var(--dash-weather-icon) 35%, transparent));
}

.time {
  font-size: 1.2rem;
  font-weight: 500;
  color: var(--accent-cyan);
  letter-spacing: 1px;
}

.basemap-toggle-btn {
  display: none;
}

.grid-nav-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  border-radius: 999px; /* 圆角胶囊 */
  background: var(--dash-btn-nav-bg);
  border: 1px solid var(--dash-btn-nav-border);
  color: var(--dash-btn-nav-text);
  cursor: pointer;
  font-weight: 700;
  letter-spacing: 1px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: auto;
  box-shadow: var(--dash-btn-nav-shadow);
  text-shadow: var(--dash-glow-text-shadow);
}

.grid-nav-btn::before {
  content: '⊞'; /* 使用网格符号代替普通的文字 */
  font-size: 1.1rem;
  color: var(--dash-accent);
}

.grid-nav-btn:hover {
  background: var(--dash-accent-soft);
  border-color: var(--dash-accent);
  box-shadow: var(--dash-btn-nav-shadow);
  transform: translateY(-1px);
}

.grid-nav-btn:active {
  transform: translateY(1px);
  box-shadow: var(--dash-btn-nav-shadow);
}

.mt-15 {
  margin-top: 15px;
  width: 100%;
  justify-content: center;
}

.glow-text {
  color: var(--dash-glow-text-color);
  text-shadow: var(--dash-glow-text-shadow);
}

.dashboard-main {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  height: auto;
  box-sizing: border-box;
  gap: 1rem;
}

/* 移动端地图容器高度优化 */
@media screen and (max-width: 768px) {
  .map-layer {
    height: calc(100dvh - 120px);
  }

  .mobile-map-container {
    height: 100% !important;
  }
}

.panel-left,
.panel-right {
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  height: 100%;
  z-index: 1;
}

.glass-panel {
  background: var(--dash-glass-bg);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid var(--dash-glass-border);
  border-radius: 12px;
  box-shadow: var(--dash-glass-shadow);
  position: relative;
  overflow: hidden;
}

.glass-panel::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--dash-glass-highlight), transparent);
}

.wrapper {
  padding: 1.2rem;
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
}

.wrapper.flex-grow {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}



.panel-title {
  font-size: 1.1rem;
  margin-top: 0;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--dash-divider);
  color: var(--text-main);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.charts-placeholder {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.stat-box {
  background: var(--dash-surface-muted);
  color: var(--dash-text-on-panel);
  padding: 0.8rem 1rem;
  border-radius: 8px;
  text-align: center;
  font-size: 0.9rem;
}

.cyan {
  color: var(--accent-cyan);
  font-weight: bold;
  font-size: 1.2rem;
}

.alert-list {
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  flex: 1;
}

.alert-item {
  padding: 0.8rem;
  margin-bottom: 0.5rem;
  border-radius: 6px;
  background: var(--dash-surface-inset);
  border-left: 3px solid;
  font-size: 0.9rem;
  color: var(--dash-text-on-panel);
}

.alert-item.error {
  border-left-color: var(--accent-red);
  color: #fca5a5;
}

.alert-item.warning {
  border-left-color: var(--accent-amber);
  color: #fde047;
}

.map-controls-overlay {
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 50;
  pointer-events: auto;
}

/* Adjust for selected device card if it's too large or overlaps */
.selected-device-card {
  z-index: 60;
  flex: 0 0 auto;
}
.weather-widget {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.2rem;
  background: var(--dash-surface-subtle);
  border-radius: 12px;
  font-size: 0.9rem;
  border: 1px solid var(--dash-divider);
  color: var(--dash-text-on-panel);
}

.weather-location {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--accent-cyan);
  margin-bottom: 0.8rem;
  text-align: center;
  text-shadow: var(--dash-glow-text-shadow);
}

.weather-main {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 0.5rem;
}

.weather-icon {
  font-size: 2.8rem;
  filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.2));
}

.weather-temp {
  font-size: 2.4rem;
  font-weight: 800;
  color: var(--dash-text-on-panel);
  letter-spacing: -1px;
}

.weather-desc {
  font-size: 1.1rem;
  color: var(--dash-text-secondary);
  margin-bottom: 1.2rem;
  font-weight: 500;
}

.weather-details {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 1.5rem;
  padding-top: 12px;
  border-top: 1px solid var(--dash-divider);
}

.detail-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
}

.detail-item .label {
  font-size: 0.75rem;
  color: var(--dash-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-item .val {
  font-size: 1rem;
  color: var(--accent-cyan);
  font-weight: 600;
}

.device-list {
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  flex: 1;
}

.device-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.8rem;
  margin-bottom: 0.5rem;
  border-radius: 6px;
  background: var(--dash-surface-muted);
  font-size: 0.85rem;
  border-left: 3px solid;
  color: var(--dash-text-on-panel);
}

.device-item.normal {
  border-left-color: #10b981;
}

.device-item.normal .device-status {
  color: #10b981;
}

.device-item.yellow {
  border-left-color: #eab308;
}

.device-item.yellow .device-status {
  color: #eab308;
}

.device-item.orange {
  border-left-color: #f97316;
}

.device-item.orange .device-status {
  color: #f97316;
}

.device-item.red {
  border-left-color: var(--accent-red);
}

.device-item.red .device-status {
  color: var(--accent-red);
}

.device-name {
  flex: 1;
  min-width: 0;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.device-type-tag {
  flex-shrink: 0;
  font-size: 0.7rem;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  background: var(--dash-surface-muted);
  min-width: 2.2em;
  text-align: center;
}

.device-status {
  flex-shrink: 0;
}

/* 统一美化滚动条 */
.alert-list::-webkit-scrollbar,
.device-list::-webkit-scrollbar {
  width: 6px;
}

.alert-list::-webkit-scrollbar-track,
.device-list::-webkit-scrollbar-track {
  background: var(--dash-scrollbar-track);
  border-radius: 4px;
}

.alert-list::-webkit-scrollbar-thumb,
.device-list::-webkit-scrollbar-thumb {
  background: var(--dash-scrollbar-thumb);
  border-radius: 4px;
}

.alert-list::-webkit-scrollbar-thumb:hover,
.device-list::-webkit-scrollbar-thumb:hover {
  background: var(--dash-scrollbar-thumb-hover);
}

/* ========== 移动端现代抽屉样式 ========== */
.mobile-drawer {
  display: none;
}

/* ========== 移动端响应式样式 ========== */
@media screen and (max-width: 768px) {
  /* 隐藏桌面端布局 */
  .desktop-layout {
    display: none !important;
  }

  /* 地图全屏 */
  .map-layer {
    height: 100dvh;
  }

  /* UI层不要拦截触摸 */
  .ui-layer {
    padding: 0;
    pointer-events: none;
  }

  .dashboard-header {
    display: grid;
    grid-template-areas: 
      "left right"
      "center center";
    grid-template-columns: 1fr auto;
    padding: 12px 16px;
    margin-bottom: 0;
    pointer-events: auto;
    background: var(--dash-header-mobile-bg);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-bottom: 1px solid var(--dash-divider);
    gap: 12px;
    max-width: 100%;
  }

  .header-left {
    grid-area: left;
    display: flex;
    align-items: center;
  }

  .header-right {
    grid-area: right;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
  }

  .header-center {
    grid-area: center;
    position: static;
    transform: none;
    width: 100%;
    text-align: center;
  }

  .map-controls-overlay {
    bottom: calc(40px + 1.5rem);
    transition: bottom 0.3s ease;
  }

  /* When drawer opens, push controls even higher or hide them? For now, let's keep it simple by just keeping them accessible above the handle. */

  .dashboard-header h1 {
    font-size: 1rem;
    letter-spacing: 0.5px;
    margin: 0;
    max-width: 100%;
  }

  .sys-logo {
    height: 32px;
    max-width: min(190px, 34vw);
  }

  .time {
    font-size: 0.85rem;
  }

  /* 现代底部抽屉 */
  .mobile-drawer {
    display: flex;
    flex-direction: column;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 100;
    background: var(--dash-drawer-bg);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-top: 1px solid var(--dash-drawer-border);
    border-radius: 24px 24px 0 0;
    transform: translateY(calc(100% - 40px));
    transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    pointer-events: auto;
    box-shadow: var(--dash-drawer-shadow);
    height: min(85dvh, calc(100dvh - 88px));
    max-height: min(85dvh, calc(100dvh - 88px));
    overflow: hidden;
    overscroll-behavior: contain;
    touch-action: pan-y;
  }

  .mobile-drawer.drawer-open {
    transform: translateY(0);
  }

  .drawer-handle-area {
    width: 100%;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: grab;
    flex-shrink: 0;
  }

  .capsule-handle {
    width: 40px;
    height: 5px;
    background: var(--dash-scrollbar-thumb);
    border-radius: 4px;
    transition: background 0.3s;
  }

  .mobile-drawer.drawer-open .capsule-handle {
    background: var(--accent-cyan);
    box-shadow: 0 0 8px var(--dash-accent-glow);
  }

  .drawer-content {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    position: relative;
    padding-bottom: env(safe-area-inset-bottom);
  }

  .drawer-scroll-area {
    height: 100%;
    max-height: 100%;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-y: contain;
    touch-action: pan-y;
    padding: 0 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .drawer-section {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .drawer-title {
    font-size: 0.85rem;
    color: var(--text-muted);
    margin: 0;
    padding-left: 4px;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 600;
  }

  /* 抽屉内部的高级玻璃卡片 */
  .glass-card {
    background: var(--dash-surface-muted);
    border: 1px solid var(--dash-divider);
    border-radius: 16px;
    padding: 16px;
    transition: background 0.2s;
  }

  .drawer-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .drawer-stats .stat-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 14px;
  }

  .stat-label {
    font-size: 0.8rem;
    color: var(--text-muted);
  }

  .mobile-primary-actions {
    gap: 0;
  }

  .mobile-grid-entry {
    width: 100%;
    min-height: 48px;
    justify-content: center;
    border-radius: 14px;
  }

  .drawer-weather {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .weather-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .weather-icon {
    font-size: 2rem;
    filter: var(--dash-logo-glow);
  }

  .weather-temp {
    font-size: 1.6rem;
    font-weight: 800;
    color: var(--dash-text-on-panel);
  }

  .weather-desc {
    color: var(--accent-cyan);
    font-size: 0.95rem;
    font-weight: 500;
  }

  .selected-device-section {
    gap: 10px;
  }

  .mobile-selected-device-card {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .mobile-selected-device-card .card-header-row {
    margin-bottom: 0;
    padding-bottom: 10px;
  }

  .mobile-selected-title {
    min-width: 0;
    margin: 0;
    color: var(--dash-text-on-panel);
    font-size: 1rem;
    line-height: 1.35;
    font-weight: 700;
    overflow-wrap: anywhere;
  }

  .mobile-selected-device-card .device-info-brief {
    gap: 8px;
  }

  .mobile-selected-device-card .brief-row {
    gap: 8px;
    font-size: 0.9rem;
  }

  .mobile-selected-device-card .brief-row .label {
    flex: 0 0 44px;
  }

  .mobile-selected-device-card .brief-row .value {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .mobile-selected-device-card .monitor-nav-btn {
    width: 100%;
    min-height: 48px;
    justify-content: center;
  }

  .mobile-selected-device-card .close-card-btn {
    flex: 0 0 44px;
    min-width: 44px;
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .project-header.glass-card {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, auto));
    justify-content: stretch;
    gap: 6px 8px;
    padding: 12px;
    margin-bottom: 8px;
    cursor: pointer;
    background: var(--dash-accent-muted);
    border-color: var(--dash-glass-border);
    border-radius: 8px;
  }

  .project-header.glass-card.active {
    background: var(--dash-accent-soft);
    border-left: 3px solid var(--accent-cyan);
  }

  .project-header.glass-card:focus-visible {
    outline: 2px solid var(--accent-cyan);
    outline-offset: 2px;
  }

  .proj-header-left {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    grid-column: 1 / -1;
    align-items: center;
    gap: 8px;
  }

  .project-name {
    font-weight: 600;
    color: var(--dash-text-on-panel);
    font-size: 15px;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .project-header .station-count {
    min-width: 0;
    font-size: 13px;
    line-height: 1.4;
    overflow-wrap: anywhere;
    color: var(--dash-text-secondary);
  }

  .project-header .station-count strong {
    font-weight: 600;
    color: var(--dash-text-on-panel);
  }

  .device-list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .project-stations {
    margin: 0 0 16px 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-left: 12px;
    border-left: 1px solid var(--dash-divider);
    margin-left: 12px;
  }

  .device-item {
    display: flex;
    align-items: center;
    padding: 10px 14px;
    background: var(--dash-surface-inset);
    border-radius: 12px;
    gap: 10px;
    border: 1px solid transparent;
  }

  .device-type-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  
  .device-type-dot.camera { background: #3b82f6; box-shadow: 0 0 8px #3b82f6; }
  .device-type-dot.sensor { background: #10b981; box-shadow: 0 0 8px #10b981; }

  .device-item.normal { border-left: 3px solid #10b981; }
  .device-item.yellow { border-left: 3px solid #eab308; }
  .device-item.orange { border-left: 3px solid #f97316; }
  .device-item.red { border-left: 3px solid var(--accent-red); }

  .device-item .device-name {
    font-size: 0.9rem;
  }
}

/* 平板端适配 */
@media screen and (min-width: 769px) and (max-width: 1024px) {
  .ui-layer {
    padding: 14px;
  }

  .dashboard-header {
    padding: 12px 20px;
    gap: 12px;
  }
  
  .dashboard-header h1 {
    max-width: min(520px, 42vw);
    font-size: 1.15rem;
  }

  .panel-left,
  .panel-right {
    width: clamp(224px, 27vw, 260px);
    gap: 1rem;
  }

  .sys-logo {
    max-width: min(260px, 28vw);
  }

  .header-right {
    gap: 10px;
  }

  .grid-nav-btn {
    min-height: 44px;
    padding: 8px 14px;
  }
  
  .wrapper {
    padding: 1rem;
  }
  
  .panel-title {
    font-size: 1rem;
  }
  
  .stat-box {
    padding: 0.6rem;
    font-size: 0.8rem;
  }

  .device-item {
    font-size: 0.8rem;
    padding: 0.5rem;
  }
  
  .weather-temp {
    font-size: 1.8rem;
  }
}

/* 小屏幕手机进一步简化 */
@media screen and (max-width: 480px) {
  .dashboard-header {
    padding: 8px 12px;
    grid-template-columns: minmax(0, 1fr) auto;
  }
  
  .dashboard-header h1 {
    font-size: 0.9rem;
    letter-spacing: 0;
  }

  .sys-logo {
    height: 24px;
    max-width: 118px;
  }

  .brand-lockup {
    max-width: 118px;
  }

  .header-left, .header-right {
    gap: 10px;
  }

  .dashboard-icon-button {
    width: 42px;
    height: 42px;
  }

  .grid-nav-btn {
    min-height: 42px;
    padding: 8px 12px;
    font-size: 0.85rem;
  }
}

/* ================== 项目列表样式 ================== */
.project-list {
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  flex: 1;
}

.project-item {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, auto));
  gap: 6px 8px;
  padding: 12px;
  margin-bottom: 0.5rem;
  background: var(--dash-surface-muted);
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;
  border-left: 3px solid transparent;
  color: var(--dash-text-on-panel);
}

.project-item:hover {
  background: var(--dash-surface-subtle);
  color: var(--dash-text-on-panel);
}

.project-item.active {
  background: var(--dash-accent-soft);
  border-left-color: var(--accent-cyan);
  color: var(--dash-text-on-panel);
  box-shadow: 0 0 10px var(--dash-accent-muted);
}

.project-item:focus-visible {
  outline: 2px solid var(--accent-cyan);
  outline-offset: 2px;
}

.project-item .project-name {
  grid-column: 1 / -1;
  min-width: 0;
  font-weight: 600;
  font-size: 15px;
  line-height: 1.4;
  white-space: normal;
  overflow-wrap: anywhere;
}

.project-item .station-count {
  min-width: 0;
  font-size: 13px;
  line-height: 1.4;
  color: var(--dash-text-secondary);
  overflow-wrap: anywhere;
}

.project-item .station-count strong {
  font-weight: 600;
  color: var(--dash-text-on-panel);
}

/* 状态颜色指示 */
.project-item.yellow { border-left-color: #eab308; }
.project-item.orange { border-left-color: #f97316; }
.project-item.red { border-left-color: var(--accent-red); }
.project-item.active { border-left-color: var(--accent-cyan); }

/* 滚动条美化 */
.project-list::-webkit-scrollbar {
  width: 6px;
}

.project-list::-webkit-scrollbar-track {
  background: var(--dash-scrollbar-track);
  border-radius: 4px;
}

.project-list::-webkit-scrollbar-thumb {
  background: var(--dash-scrollbar-thumb);
  border-radius: 4px;
}

.project-list::-webkit-scrollbar-thumb:hover {
  background: var(--dash-scrollbar-thumb-hover);
}

.selected-device-card {
  margin-top: 0;
  animation: slideIn 0.3s ease-out;
}

.panel-right.has-selected-device {
  gap: 0.75rem;
}

.panel-right.has-selected-device .weather-panel {
  padding: 0.85rem;
}

.panel-right.has-selected-device .weather-widget {
  padding: 0.75rem;
}

.panel-right.has-selected-device .weather-location {
  margin-bottom: 0.35rem;
  font-size: 0.95rem;
}

.panel-right.has-selected-device .weather-main {
  margin-bottom: 0.2rem;
}

.panel-right.has-selected-device .weather-temp {
  font-size: 1.8rem;
}

.panel-right.has-selected-device .weather-desc {
  margin-bottom: 0.55rem;
  font-size: 0.9rem;
}

.panel-right.has-selected-device .weather-details {
  padding-top: 8px;
}

.device-info-brief {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.brief-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  font-size: 0.9rem;
  min-height: 1.5em;
}

.brief-row .label {
  color: var(--dash-text-muted);
  flex-shrink: 0;
  min-width: 3em;
}
.brief-row .value {
  color: var(--dash-text-on-panel);
  font-weight: 500;
  text-align: right;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
.brief-row .sid { font-family: monospace; color: var(--accent-cyan); }

.status-tag {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
}
.status-tag.normal { background: rgba(16, 185, 129, 0.2); color: #10b981; }
.status-tag.yellow { background: rgba(234, 179, 8, 0.2); color: #eab308; }
.status-tag.orange { background: rgba(249, 115, 22, 0.2); color: #f97316; }
.status-tag.red { background: rgba(239, 68, 68, 0.2); color: #ef4444; }

.monitor-nav-btn {
  width: 100%;
  padding: 10px;
  background: var(--dash-btn-nav-bg);
  border: 1px solid var(--accent-cyan);
  color: var(--dash-btn-nav-text);
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.monitor-nav-btn:hover {
  background: var(--dash-accent-soft);
  box-shadow: var(--dash-btn-nav-shadow);
}

@keyframes slideIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.card-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid var(--dash-divider);
}

.card-header-row .panel-title {
  margin-bottom: 0;
  border-bottom: none;
  padding-bottom: 0;
}

.close-card-btn {
  background: none;
  border: none;
  color: var(--dash-text-muted);
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
  transition: all 0.2s;
}

.close-card-btn:hover {
  color: var(--accent-cyan);
  transform: scale(1.1);
}
</style>
